import { Bank, CaretLeft, CheckCircle, Copy, IdentificationCard } from "@phosphor-icons/react";
import useVirtualAccount from "../../hooks/useVirtualAccount";
import { HashLoader } from "react-spinners";
import useGenerateVirtualAccount from "../../hooks/useGenerateVirtualAccount";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { readSetupTaskState } from "../../utils/setupTaskReturn";

const VirtualAccount = () => {
  const { virtualAccount, isLoading } = useVirtualAccount();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { returnTo, returnState } = readSetupTaskState(location.state);

  const bvnVerified = Number(user?.compliance?.bvn_verified) === 1;

  const handleCopy = () => {
    if (!virtualAccount?.data?.account_number) return;

    navigator.clipboard.writeText(virtualAccount?.data.account_number);
  };

  const handleShare = () => {
    if (navigator.share && virtualAccount?.data) {
      navigator
        .share({
          title: "Account Details",
          text: `Account Name: ${virtualAccount?.data.account_name.replace(
            "\\",
            ""
          )}\nAccount Number: ${
            virtualAccount?.data.account_number
          }\nBank Name: ${virtualAccount?.data.bank_name}`,
        })
        .then(() => {
          toast.success("Account details shared successfully");
        })
        .catch((error) => {});
    } else {
    }
  };

  const {
    generateFundingWallet,
    generatingFundingWallet,
    generatingFundingWalletError,
    isFundingWalletError,
  } = useGenerateVirtualAccount({
    onSuccess() {
      toast.success("Your virtual account has been created.");
    },
    onError(error) {
      toast.error(error.message);
    },
  });
  if (!bvnVerified) {
    return (
      <div className="flex flex-col items-start gap-5 px-5 py-5 pb-20 max-w-3xl mx-auto w-full">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
        >
          <CaretLeft weight="bold" />
          <span>Back</span>
        </button>
        <div className="bg-white border border-gray-200 rounded-xl p-6 w-full flex flex-col gap-5">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-50 rounded-full text-amber-500 shrink-0">
              <IdentificationCard size={32} weight="fill" />
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-xl font-semibold">BVN Required</div>
              <div className="text-gray-500">
                You need to verify your BVN before you can create a virtual account for bank transfers. Your BVN is used to securely link your bank identity to your wallet.
              </div>
            </div>
          </div>
          <button
            onClick={() =>
              navigate("/verify-bvn", {
                state: { background: location, name: "verify-bvn" },
              })
            }
            className="p-4 bg-deepBlue text-white rounded-xl font-medium hover:bg-deepBlue/90 transition"
          >
            Verify your BVN
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {isLoading ? (
        <div className="z-50 flex items-center justify-center p-20">
          <HashLoader />
        </div>
      ) : virtualAccount &&
        virtualAccount?.data &&
        virtualAccount?.data?.account_number &&
        virtualAccount?.data?.account_name ? (
        <div className="flex flex-col items-start gap-5 px-5 py-5 pb-20">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
          >
            <CaretLeft weight="bold" />
            <span>Back</span>
          </button>

          {returnTo && (
            <div className="max-w-3xl mx-auto w-full rounded-lg border border-visaro-orange/30 bg-visaro-orange/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 text-sm text-deepBlue">
                Transfer to this account, then go back to your booking. Your
                balance updates once the transfer arrives.
              </div>
              <button
                type="button"
                onClick={() => navigate(returnTo, { replace: true, state: returnState ?? null })}
                className="p-2 py-3 px-5 rounded-xl font-medium bg-deepBlue text-white text-sm whitespace-nowrap"
              >
                Back to my booking
              </button>
            </div>
          )}

          <div className="border border-gray-200 rounded-lg p-5 max-w-3xl mx-auto w-full">
            <div className="flex flex-col">
              <div className="flex items-start md:items-center gap-4 flex-col md:flex-row">
                <div className="p-2 bg-visaro-orange/10 rounded-full">
                  <Bank size={32} className="text-visaro-orange" />
                </div>
                <div className="flex flex-col">
                  <div className="text-xl font-medium">
                    Your Virtual Account
                  </div>
                  <div className="text-gray-500">
                    This is your unique account number. Use it to fund your
                    wallet directly.
                  </div>
                </div>
              </div>
              <hr className="my-4 border-dashed" />
              <div>
                <div className="text-gray-500">
                  {virtualAccount?.data?.account_name?.replace("\\", "")}
                </div>
                <div className="text-2xl font-bold flex items-center gap-2">
                  <span>{virtualAccount?.data?.account_number}</span>
                  <button
                    onClick={handleCopy}
                    type="button"
                    className="p-2 rounded-md hover:bg-gray-50 transition-all"
                  >
                    <Copy
                      size={18}
                      weight="fill"
                      className="text-gray-500 hover:text-visaro-orange transition-all"
                    />
                  </button>
                </div>
                <div className="text-gray-500">
                  {virtualAccount?.data?.bank_name}
                </div>
              </div>
              <br />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="p-3 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm disabled:opacity-50"
                >
                  Copy
                </button>
                <button
                  onClick={handleShare}
                  className="p-3 px-5 w-full border-deepBlue/20 border rounded-lg font-bold bg-transparent text-deepBlue text-sm disabled:opacity-50"
                >
                  Share Details
                </button>
              </div>
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-5 max-w-3xl mx-auto w-full">
            <div>Add money via bank transfer in just few steps</div>
            <div className="flex flex-col gap-4 mt-4">
              <div className="flex items-start gap-4">
                <span className="text-visaro-orange">1.</span>
                <div className="flex gap-1 flex-col items-start ">
                  Copy your unique account number and use it to fund your
                  wallet.
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-visaro-orange">2.</span>
                <div className="flex gap-1 flex-col items-start ">
                  Wait for the payment to reflect in your wallet.
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-visaro-orange">3.</span>
                <div className="flex gap-1 flex-col items-start ">
                  Once the payment is confirmed, you will receive a
                  notification.
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-col gap-4 flex items-start justify-center max-w-3xl mx-auto w-full py-10 px-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 self-start"
          >
            <CaretLeft weight="bold" />
            <span>Back</span>
          </button>
          <div className="bg-white p-5 border border-gray-200 rounded-md w-full flex flex-col gap-5">
            <div className="flex items-start md:items-center gap-4 flex-col md:flex-row">
              <div className="p-2 bg-visaro-orange/10 rounded-full">
                <Bank size={32} className="text-visaro-orange" />
              </div>
              <div className="flex flex-col">
                <div className="text-xl font-medium">
                  You currently do not have a virtual account. Why create one?
                </div>
                <div className="text-gray-500">
                  Creating a virtual account is quick and easy. Enjoy seamless
                  transactions and instant wallet funding.
                </div>
              </div>
            </div>
            <hr className="border-dashed" />
            <div className="flex items-start gap-4">
              <CheckCircle
                size={24}
                weight="fill"
                className="text-visaro-orange"
              />
              <div className="flex flex-col items-start ">
                <div className="text-lg font-medium">Instant Funding</div>
                <div>
                  Your wallet gets credited immediately after payment—no delays.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle
                size={24}
                weight="fill"
                className="text-visaro-orange"
              />
              <div className="flex flex-col items-start">
                <div className="text-lg font-medium">Unique Account Number</div>
                <div>
                  Get your personalized account number linked directly to you
                  wallet.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle
                size={24}
                weight="fill"
                className="text-visaro-orange"
              />
              <div className="flex flex-col items-start ">
                <div className="text-lg font-medium">No Hassle Transfers</div>
                <div>
                  Send money from any bank or payment app straight into your
                  wallet.
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                disabled={generatingFundingWallet}
                onClick={() => generateFundingWallet({})}
                type="submit"
                className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm disabled:opacity-50"
              >
                {generatingFundingWallet ? (
                  <HashLoader size={12} color="#ffffff" className="mx-auto" />
                ) : (
                  "Create Virtual Account"
                )}
              </button>
              {isFundingWalletError && (
                <div className="text-red-500 text-left flex">
                  {generatingFundingWalletError?.message}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VirtualAccount;
